import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import { Link, type LinkProps } from "react-router";
import cn from "~/utils/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";
type Common = { variant?: Variant; size?: Size; block?: boolean; className?: string; children: ReactNode };
type Props =
  | (Common & { to: LinkProps["to"] } & Omit<LinkProps, "to" | "className">)
  | (Common & { href: string } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className">)
  | (Common & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className">);

const variants: Record<Variant, string> = {
  primary: "bg-accent text-on-accent hover:bg-accent-press",
  secondary: "bg-surface text-ink border border-line-2 hover:border-accent-text",
  ghost: "text-ink hover:bg-accent-soft",
  danger: "bg-danger-soft text-danger hover:brightness-110",
};
const sizes: Record<Size, string> = {
  sm: "h-9 px-4 text-sm",
  md: "h-11 px-5 text-[15px]",
  lg: "h-14 px-7 text-base",
};

export function buttonClass({ variant = "primary", size = "md", block, className }: Omit<Common, "children">) {
  return cn(
    "inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-btn font-bold leading-none",
    "transition-[transform,background-color,border-color,filter] duration-200 ease-out active:scale-[0.98]",
    "disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50",
    variants[variant],
    sizes[size],
    block && "w-full",
    className,
  );
}

export default function Button(props: Props) {
  const { variant, size, block, className, children, ...rest } = props as Common & Record<string, any>;
  const cls = buttonClass({ variant, size, block, className });
  if ("to" in rest && rest.to !== undefined)
    return (
      <Link className={cls} {...(rest as LinkProps)}>
        {children}
      </Link>
    );
  if ("href" in rest && rest.href !== undefined)
    return (
      <a className={cls} {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}>
        {children}
      </a>
    );
  return (
    <button type="button" className={cls} {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}>
      {children}
    </button>
  );
}
